// Vendor
import PropTypes from 'prop-types';
import React from 'react';

// Components
import Button, {
  ButtonDisabledStyle,
  ButtonSize,
  ButtonVariant
} from 'src/components/atoms/Button';
import Gap from 'src/components/atoms/Gap';
import Grid from 'src/components/atoms/Grid';
import { Icon } from 'src/components/atoms/Icon';
import { Line } from 'src/components/atoms/Line';
import Text, { TextAlignItems, TextVariant } from 'src/components/atoms/Text';
import { StyledContainer, StyledGrid } from './styles';

//Hooks
import { useGenerateApplicantLink } from 'src/features/identityVerification/hooks/useGenerateApplicantLink';
import { useQrCodeGenerator } from 'src/features/identityVerification/hooks/useQrCodeGenerator';
import useHidePortalLinks from 'src/features/propertyFeature/hooks/useHidePortalLinks';
import { useFeatureFlag } from 'src/hooks';

// Types
import { IProperty } from 'src/ts/interfaces';

//Redux
import {
  EMPTY_VALUE,
  IDV_FLOW_TYPE,
  IDV_LABEL,
  IDV_PROVIDER,
  toWireFlowType,
  UI_BUTTON_LABELS
} from 'src/features/property';
import { Feature, FeatureFlagKey, FeatureState, LinkAction } from 'src/ts/enums';

export type IdentityVerificationLinkRowProps = Pick<
  IProperty,
  | 'name'
  | 'address'
  | 'city'
  | 'state'
  | 'zip'
  | 'short_id'
  | 'company_short_id'
  | 'identity_verification_provider'
  | 'identity_verification_flow_types'
  | 'property_features'
> & {
  company_name?: string;
};

const IdentityVerificationLinkRow: React.FC<IdentityVerificationLinkRowProps> = (props) => {
  const {
    short_id: propertyShortId,
    identity_verification_provider,
    identity_verification_flow_types,
    property_features
  } = props;
  const clearFlowTypes = new Set(identity_verification_flow_types || []);
  const { onRedirectApplicantLink } = useGenerateApplicantLink();
  const { hidePropertyLinks } = useHidePortalLinks();
  const hideCopyLink = hidePropertyLinks(property_features);

  const cpnFraudFF = useFeatureFlag(FeatureFlagKey.CPN_FRAUD);
  const isClearProvider = identity_verification_provider === IDV_PROVIDER.CLEAR;

  // Transitional: the Clear term is Clear-only until Incode gains CPN (epic AIC-1232).
  const isCpnEnabled =
    cpnFraudFF &&
    isClearProvider &&
    property_features?.some(
      (feature) =>
        feature.code === Feature.CPN_FRAUD_CHECK && feature.state === FeatureState.ENABLED
    ) === true;

  const { generateQrCode, generateIncodeQrCode } = useQrCodeGenerator({
    propertyShortId: propertyShortId || EMPTY_VALUE,
    propertyName: props.name,
    companyName: props.company_name,
    isCpnEnabled
  });

  const handleRedirectLink = (type: 'enhanced' | 'basic' | undefined, action: LinkAction) => {
    onRedirectApplicantLink(
      propertyShortId || EMPTY_VALUE,
      action,
      type ? toWireFlowType(type, isCpnEnabled) : undefined
    );
  };

  const renderClearLinkRow = (label: string, type: 'basic' | 'enhanced') => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <div>
        <Text isBold variant={TextVariant.small} color="initial">
          {label}&nbsp;:
        </Text>
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {!hideCopyLink && (
          <Button
            name={`button-idv-${type}-generate-link-copy`}
            variant={ButtonVariant.ghost}
            size={ButtonSize.small}
            onClick={() => handleRedirectLink(type, LinkAction.COPY)}
            disabledStyle={ButtonDisabledStyle.whiteWithBorder}
          >
            <Text
              variant={TextVariant.small}
              color="initial"
              style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
            >
              <Icon icon="filterNone" />
              {UI_BUTTON_LABELS.COPY_LINK}
            </Text>
          </Button>
        )}
        <Button
          name={`button-idv-${type}-generate-link-qr`}
          variant={ButtonVariant.ghost}
          size={ButtonSize.small}
          onClick={() => generateQrCode(type)}
          disabledStyle={ButtonDisabledStyle.whiteWithBorder}
        >
          <Text
            variant={TextVariant.small}
            color="initial"
            style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
          >
            <Icon icon="qr_code" />
            {UI_BUTTON_LABELS.QR_CODE}
          </Text>
        </Button>
      </div>
    </div>
  );

  const clearButtonContainer = (
    <div>
      {clearFlowTypes.has(IDV_FLOW_TYPE.BASIC) &&
        renderClearLinkRow(IDV_LABEL.BASIC, IDV_FLOW_TYPE.BASIC)}
      {clearFlowTypes.has(IDV_FLOW_TYPE.ENHANCED) &&
        renderClearLinkRow(IDV_LABEL.ENHANCED, IDV_FLOW_TYPE.ENHANCED)}
    </div>
  );
  const defaultButtonContainer = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div>
          <Text isBold variant={TextVariant.small} color="initial">
            {IDV_LABEL.ENHANCED}&nbsp;:
          </Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {!hideCopyLink && (
            <Button
              name="button-idv-incode-generate-link-copy"
              variant={ButtonVariant.ghost}
              size={ButtonSize.small}
              onClick={() => handleRedirectLink(undefined, LinkAction.COPY)}
              disabledStyle={ButtonDisabledStyle.whiteWithBorder}
            >
              <Text
                variant={TextVariant.small}
                color="initial"
                style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
              >
                <Icon icon="filterNone" />
                {UI_BUTTON_LABELS.COPY_LINK}
              </Text>
            </Button>
          )}
          <Button
            name="button-idv-incode-generate-qr"
            variant={ButtonVariant.ghost}
            size={ButtonSize.small}
            onClick={() => generateIncodeQrCode()}
            disabledStyle={ButtonDisabledStyle.whiteWithBorder}
          >
            <Text
              variant={TextVariant.small}
              color="initial"
              style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
            >
              <Icon icon="qr_code" />
              {UI_BUTTON_LABELS.QR_CODE}
            </Text>
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <StyledContainer>
      <Line height="tiny" />
      <Gap height={1} />
      <StyledGrid direction="column">
        <Grid direction="column">
          <Grid direction="column" justify="space-between">
            <Text isBold alignItems={TextAlignItems.center} color="initial">
              {props.name}
            </Text>
            {props.company_name && (
              <Text variant={TextVariant.small} color="gray700">
                {`Company Name: ${props.company_name}`}
              </Text>
            )}
          </Grid>
        </Grid>
        <Gap height={0.1875} />
        <Text variant={TextVariant.small} color={'gray700'}>
          {`${props.address}, ${props.city}, ${props.state} ${props.zip}`}
        </Text>
        <Gap height={0.5} />
        {isClearProvider ? clearButtonContainer : defaultButtonContainer}
        {/* <Gap height={0.5} /> */}
      </StyledGrid>
    </StyledContainer>
  );
};

IdentityVerificationLinkRow.propTypes = {
  name: PropTypes.string.isRequired,
  address: PropTypes.string.isRequired,
  city: PropTypes.string.isRequired,
  state: PropTypes.string.isRequired,
  zip: PropTypes.string.isRequired,
  short_id: PropTypes.string.isRequired,
  company_short_id: PropTypes.string.isRequired,
  company_name: PropTypes.string
};

export default IdentityVerificationLinkRow;
